import React, { useEffect, useMemo } from "react";
import type { FC, SyntheticEvent } from "react";
import cn from "classnames";
import {
  api,
  foundationUserApi,
  useAppDispatch,
  useAppSelector,
} from "@litres-core/store";
import { getCookie, setCookie } from "@litres/utils";
import { ConfirmPanel } from "./ConfirmPanel/ConfirmPanel";
import { SuccessPanel } from "./SuccessPanel/SuccessPanel";
import { trackerSafeModeClick, trackerSafeModeShow } from "./analytics";
import { useSafemode } from "./useSafemode";
import { ESteps, ESafemode, ESafeModeStatus } from "./constants";
import styles from "./Safemode.module.scss";
export interface ISafeModeProps {
  analyticsData: { internalSource: string };
  customClass?: string;
  isVisible?: boolean;
  onSafeModeApply?: () => void;
  customContent?: React.ReactNode;
}

const DAY_IN_MS = 24 * 60 * 60 * 1000;

export const Safemode: FC<ISafeModeProps> = ({
  customClass = "",
  analyticsData,
  isVisible = true,
  onSafeModeApply,
  customContent,
}) => {
  // синхронизируем настройки, в зависимости от типа пользователя
  const { safeModeStatus, changeSafeMode } = useSafemode();
  type ActiveStepType = keyof typeof ESteps;
  const dispatch = useAppDispatch();
  const { internalSource = "" } = analyticsData || {};
  const activeStep = useAppSelector(
    state => state.foundationUser.safemode_active_step
  );
  const [setSafemode] = foundationUserApi.useSetSafemodeMutation();

  const setActiveStep = (step: ActiveStepType) => {
    dispatch(api.foundationUser.setFoundationUserSafemodeStep(step));
  };

  useEffect(() => {
    if (activeStep === ESteps.close) {
      const expires = new Date(Date.now() + DAY_IN_MS);
      setCookie("isClosedSafeMode", "1", {
        expires,
      });
    }
    if (!activeStep) {
      setActiveStep("confirm");
    }
  }, [activeStep]);

  const user = useAppSelector(state => state.foundationUser);
  const isAuthenticated = useMemo(() => user.id && user.login, [user]);
  const isClosedSafeModeCookie = getCookie("isClosedSafeMode");

  const hasSafemodeValue = safeModeStatus !== ESafeModeStatus.undefined;

  // отправляем событие аналитики при показе плашки
  useEffect(() => {
    if (internalSource && activeStep === ESteps.confirm && !hasSafemodeValue) {
      trackerSafeModeShow({ internalSource });
    }
  }, [internalSource, activeStep, hasSafemodeValue]);

  const changeSafeModeAccountSettings = (value: ESafemode) => {
    setSafemode({ is_safemode_enabled: value === ESafemode.hide })
      .unwrap()
      .then(() => {
        changeSafeMode(!!value);
      })
      .catch(e => {
        console.error("Ошибка", e.error?.title);
      });
  };

  const handleOnApply = (e: SyntheticEvent): void => {
    e.stopPropagation();
    setActiveStep(ESteps.success);

    // отправляем событие аналитики при установке значения плашки
    if (internalSource) {
      trackerSafeModeClick({ internalSource, isActive: !!ESafemode.hide });
    }

    if (isAuthenticated) {
      changeSafeModeAccountSettings(ESafemode.hide);
    } else {
      changeSafeMode(true);
    }
    if (onSafeModeApply) {
      onSafeModeApply();
    }
  };

  const handleOnCancel = (e: SyntheticEvent) => {
    e.stopPropagation();
    setActiveStep(ESteps.success);

    // отправляем событие аналитики при установке значения плашки
    if (internalSource) {
      trackerSafeModeClick({ internalSource, isActive: !!ESafemode.show });
    }

    if (isAuthenticated) {
      changeSafeModeAccountSettings(ESafemode.show);
    } else {
      changeSafeMode(false);
    }
  };

  const handleOnCLose = (e: SyntheticEvent) => {
    e.stopPropagation();
    setActiveStep(ESteps.close);
  };

  useEffect(() => {
    if (
      isClosedSafeModeCookie !== "1" &&
      (activeStep === ESteps.close ||
        !isVisible ||
        (hasSafemodeValue && activeStep !== ESteps.success))
    ) {
      const expires = new Date(Date.now() + DAY_IN_MS);
      setCookie("isClosedSafeMode", "1", {
        expires,
      });
    }
  }, []);

  if (
    activeStep === ESteps.close ||
    isClosedSafeModeCookie === "1" ||
    !isVisible ||
    (hasSafemodeValue && activeStep !== ESteps.success)
  ) {
    return null;
  }

  return (
    <>
      {customContent}
      <div
        className={cn(styles.safemodeContainer, customClass)}
        data-testid="safemode__container"
      >
        {activeStep === ESteps.confirm && (
          <ConfirmPanel
            onApply={handleOnApply}
            onCancel={handleOnCancel}
            onClose={handleOnCLose}
          />
        )}
        <SuccessPanel
          isVisible={activeStep === ESteps.success}
          onClose={handleOnCLose}
        />
      </div>
    </>
  );
};

export default Safemode;
